import { useEffect, useRef } from 'react';
import { getCoords } from 'root/utils/getCoords';

interface useShadowOptions {
  scale?: number;
}

interface CallbackProps {
  factor: number;
  element: HTMLElement;
}

type CallbackType = (props: CallbackProps) => void;

interface useShadowProps {
  classNames: string;
  callback?: CallbackType;
  options?: useShadowOptions;
}

const useShadow = ({ callback, classNames, options = {} }: useShadowProps) => {
  const elements = useRef<HTMLElement[]>([]);
  const shadowRef = useRef<HTMLDivElement>(null);
  const animation = useRef<number | null>(null);

  const moveHandler = () => {
    if (!elements.current.length) {
      return;
    }
    for (let el of elements.current) {
      let { offsetCenterX } = getCoords(el);
      let factor = Math.abs(offsetCenterX * (options.scale || 1)) / window.innerWidth;

      if (callback) {
        callback({
          element: el,
          factor,
        });
      }
    }

    animation.current = requestAnimationFrame(moveHandler);
  };

  useEffect(() => {
    if (shadowRef.current) {
      elements.current = Array.from(shadowRef.current.querySelectorAll(`.${classNames}`));
      animation.current = requestAnimationFrame(moveHandler);
    }

    return () => {
      if (animation.current !== null) {
        cancelAnimationFrame(animation.current);
        animation.current = null;
        elements.current = [];
      }
    };
  }, [shadowRef.current]);

  return { shadowRef };
};

export default useShadow;
